/**
 * Theme Context and Provider
 *
 * Gerencia o theme da aplicacao usando React Context.
 * Gera CSS variables dinamicamente baseado no theme selecionado.
 *
 * @since 2025-12-16
 * @epic INIT-0045-03, INIT-0046
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
} from 'react';
import type { Theme, ThemeContextValue } from './types';
import { themes, defaultTheme, availableThemeIds, getThemeById } from './themes';
import { generateCssVariables, applyCssVariables } from './cssVariables';
import { fetchThemeConfig } from '@/services/configService';
import { BootLoader } from '@/components/ui/BootLoader';

// ==========================================
// CONTEXT
// ==========================================

const ThemeContext = createContext<ThemeContextValue | null>(null);

// ==========================================
// STORAGE KEYS
// ==========================================

const STORAGE_KEYS = {
  THEME_ID: 'tmf_theme_id',
  DARK_MODE: 'tmf_dark_mode',
} as const;

// ==========================================
// PROVIDER PROPS
// ==========================================

interface ThemeProviderProps {
  children: React.ReactNode;
  /** Theme inicial (opcional, usa localStorage ou default) */
  initialTheme?: string;
  /** Desabilitar persistencia em localStorage */
  disablePersistence?: boolean;
}

// ==========================================
// PROVIDER
// ==========================================

export function ThemeProvider({
  children,
  initialTheme,
  disablePersistence = false,
}: ThemeProviderProps) {
  // Carregar theme inicial
  const getInitialTheme = (): Theme => {
    if (initialTheme && themes[initialTheme]) {
      return themes[initialTheme];
    }

    if (!disablePersistence && typeof window !== 'undefined') {
      const storedId = localStorage.getItem(STORAGE_KEYS.THEME_ID);
      if (storedId && themes[storedId]) {
        return themes[storedId];
      }
    }

    return defaultTheme;
  };

  const getInitialDarkMode = (): boolean => {
    if (!disablePersistence && typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEYS.DARK_MODE);
      if (stored !== null) {
        return stored === 'true';
      }
      // Verificar preferencia do sistema
      return window.matchMedia?.('(prefers-color-scheme: dark)').matches || false;
    }
    return false;
  };

  const [theme, setThemeState] = useState<Theme>(getInitialTheme);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(getInitialDarkMode);
  // Quando persistência está desabilitada (ex.: ambiente de testes), não bloqueamos
  // o render aguardando a API de tema — isso evita splash screen travar os testes.
  const [isLoading, setIsLoading] = useState(!disablePersistence);

  // Função para carregar/recarregar tema da API
  const loadThemeFromApi = useCallback(async () => {
    try {
      const apiTheme = await fetchThemeConfig();
      if (apiTheme) {
        // Mesclar com tema local (API sobrescreve valores)
        setThemeState((currentTheme) => ({
          ...currentTheme,
          ...apiTheme,
          // Garantir que background da API sobrescreva
          background: {
            ...currentTheme.background,
            ...apiTheme.background,
          },
          // Garantir que logos da API sobrescrevam
          logos: {
            ...currentTheme.logos,
            ...apiTheme.logos,
          },
          // Garantir que cores da API sobrescrevam
          colors: {
            ...currentTheme.colors,
            ...apiTheme.colors,
          },
          // Garantir que blocos da API sobrescrevam (cores alternadas)
          blocks: {
            block1: {
              ...currentTheme.blocks.block1,
              ...apiTheme.blocks?.block1,
            },
            block2: {
              ...currentTheme.blocks.block2,
              ...apiTheme.blocks?.block2,
            },
          },
          // Garantir que menus da API sobrescrevam
          menus: {
            main: {
              ...currentTheme.menus.main,
              ...apiTheme.menus?.main,
            },
            submenu: {
              ...currentTheme.menus.submenu,
              ...apiTheme.menus?.submenu,
            },
          },
          // Garantir que botões da API sobrescrevam
          buttons: {
            ...currentTheme.buttons,
            ...apiTheme.buttons,
          },
        }));
      }
    } catch (error) {
      if (import.meta.env.DEV) console.warn('Could not load theme from API, using default:', error);
    }
  }, []);

  // Carregar configuração do tema da API ao montar
  useEffect(() => {
    // Em ambientes sem persistência (testes), pula o fetch da API para não
    // travar o render aguardando uma Promise que pode não resolver antes dos asserts.
    if (disablePersistence) return;

    let mounted = true;

    async function initialLoad() {
      await loadThemeFromApi();
      if (mounted) {
        setIsLoading(false);
      }
    }

    initialLoad();

    return () => {
      mounted = false;
    };
  }, [loadThemeFromApi, disablePersistence]);

  // Resolve variantes de imagem com base no modo (dark/light)
  // Se estiver em dark mode e houver variante dark, usa ela; senão fallback para light
  const resolvedTheme = useMemo<Theme>(() => {
    if (!isDarkMode) return theme;

    const pick = (light: string, dark: string | undefined): string =>
      dark || light;

    return {
      ...theme,
      background: {
        ...theme.background,
        color: pick(theme.background.color, theme.background.colorDark),
        image: pick(theme.background.image, theme.background.imageDark),
        loginImage: pick(theme.background.loginImage || '', theme.background.loginImageDark),
      },
      colors: {
        ...theme.colors,
        // Dark variants resolvidos para os campos base
        // (cssVariables.ts já usa os campos *Dark diretamente, mas aqui
        // garantimos que componentes que acessam theme.colors diretamente
        // também recebam o valor correto)
        topbarBackground: pick(theme.colors.topbarBackground, theme.colors.topbarBackgroundDark),
        topbarText: pick(theme.colors.topbarText, theme.colors.topbarTextDark),
        topbarBorder: pick(theme.colors.topbarBorder, theme.colors.topbarBorderDark),
        sidebarBackground: pick(theme.colors.sidebarBackground, theme.colors.sidebarBackgroundDark),
        footerBackground: pick(theme.colors.footerBackground, theme.colors.footerBackgroundDark),
        footerText: pick(theme.colors.footerText, theme.colors.footerTextDark),
        footerBorder: pick(theme.colors.footerBorder, theme.colors.footerBorderDark),
        emailLink: pick(theme.colors.emailLink, theme.colors.emailLinkDark),
      },
      logos: {
        ...theme.logos,
        principal: pick(theme.logos.principal, theme.logos.principalDark),
        menu: pick(theme.logos.menu, theme.logos.menuDark),
        campanha: pick(theme.logos.campanha, theme.logos.campanhaDark),
        loading: pick(theme.logos.loading, theme.logos.loadingDark),
      },
      icons: {
        ...theme.icons,
        favicon: pick(theme.icons.favicon, theme.icons.faviconDark),
      },
    };
  }, [theme, isDarkMode]);

  // Aplicar CSS variables e classe dark ANTES do browser pintar.
  // useLayoutEffect garante que CSS variables, classe dark do Tailwind e inline styles
  // estejam todos sincronizados no mesmo frame, evitando flash/perda de cores ao trocar modo.
  //
  // Fluxo de overlay por tenant (TMF9-56970):
  //   1. generateCssVariables() converte os campos estruturados do Theme (colors, blocks,
  //      menus, etc.) nas 54+ CSS vars do contrato definido em CONTRATO-VARIAVEIS-THEMEABLE.md.
  //   2. applyCssVariables() injeta todas as vars no :root via inline style (maior precedência).
  //   3. customVariables (campo opcional do Theme) permite ao backend retornar vars extras
  //      que sobrepõem a base default sem passar pelo parser estruturado.
  //   4. Ao chamar refreshTheme(), o state muda → este efeito re-executa → vars são
  //      re-aplicadas com os valores do novo tema, sobrescrevendo o tenant anterior.
  useLayoutEffect(() => {
    const cssVars = generateCssVariables(resolvedTheme, isDarkMode);
    applyCssVariables(cssVars);

    // Aplicar variáveis customizadas extras do tenant (overlay direto no :root).
    // Executado após generateCssVariables() para garantir que vars extras têm
    // precedência sobre as vars geradas estruturalmente.
    // @epic TMF9-56963 | @issue TMF9-56970
    if (resolvedTheme.customVariables && Object.keys(resolvedTheme.customVariables).length > 0) {
      applyCssVariables(resolvedTheme.customVariables);
    }

    // Atualizar classe no body para dark mode (CSS custom)
    document.body.classList.toggle('dark-mode', isDarkMode);
    document.body.classList.toggle('light-mode', !isDarkMode);

    // Atualizar classe no html para Tailwind dark: classes
    document.documentElement.classList.toggle('dark', isDarkMode);
  }, [resolvedTheme, isDarkMode]);

  // Aplicar favicon e custom CSS (pode ser useEffect normal, não afeta layout)
  useEffect(() => {
    // Atualizar favicon dinamicamente quando o tema mudar
    const faviconUrl = resolvedTheme.icons?.favicon;
    if (faviconUrl) {
      let link = document.querySelector<HTMLLinkElement>("link[rel*='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = faviconUrl;
    }

    // Aplicar custom CSS se existir
    if (resolvedTheme.customCss) {
      let styleEl = document.getElementById('tmf-theme-custom-css');
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = 'tmf-theme-custom-css';
        document.head.appendChild(styleEl);
      }
      styleEl.textContent = resolvedTheme.customCss;
    }
  }, [resolvedTheme]);

  // Persistir theme
  useEffect(() => {
    if (!disablePersistence) {
      localStorage.setItem(STORAGE_KEYS.THEME_ID, theme.id);
    }
  }, [theme.id, disablePersistence]);

  // Persistir dark mode
  useEffect(() => {
    if (!disablePersistence) {
      localStorage.setItem(STORAGE_KEYS.DARK_MODE, String(isDarkMode));
    }
  }, [isDarkMode, disablePersistence]);

  // Listener para preferencia de sistema
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      // Só auto-atualiza se não tiver preferencia salva
      if (!localStorage.getItem(STORAGE_KEYS.DARK_MODE)) {
        setIsDarkMode(e.matches);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Funções do contexto
  const setTheme = useCallback((themeId: string) => {
    const newTheme = getThemeById(themeId);
    if (newTheme) {
      setThemeState(newTheme);
    } else {
      if (import.meta.env.DEV) console.warn(`Theme "${themeId}" not found. Available: ${availableThemeIds.join(', ')}`);
    }
  }, []);

  const toggleDarkMode = useCallback(() => {
    setIsDarkMode((prev) => !prev);
  }, []);

  const getCssVariable = useCallback(
    (variable: string): string => {
      // Tenta pegar do computed style
      const root = document.documentElement;
      const varName = variable.startsWith('--') ? variable : `--${variable}`;
      return getComputedStyle(root).getPropertyValue(varName).trim();
    },
    []
  );

  // Valor do contexto
  const contextValue = useMemo<ThemeContextValue>(
    () => ({
      theme: resolvedTheme,
      themeId: resolvedTheme.id,
      availableThemes: availableThemeIds,
      setTheme,
      getCssVariable,
      isDarkMode,
      toggleDarkMode,
      isLoading,
      refreshTheme: loadThemeFromApi,
    }),
    [resolvedTheme, setTheme, getCssVariable, isDarkMode, toggleDarkMode, isLoading, loadThemeFromApi]
  );

  // Bloquear renderização até o tema carregar da API
  // Isso garante que o tema é a fonte única de verdade e evita flash de cores erradas.
  // Usa o BootLoader — a MESMA tela dos demais estágios do boot — para que a
  // troca de estágio não pisque. Antes esta tela pintava #2a4d62 fixo e trazia
  // um spinner inline próprio, divergindo dos outros loaders [TMF9-61077].
  if (isLoading) {
    return (
      <ThemeContext.Provider value={contextValue}>
        <BootLoader fullscreen />
      </ThemeContext.Provider>
    );
  }

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}

// ==========================================
// HOOK
// ==========================================

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
}

// ==========================================
// EXPORTS
// ==========================================

export { ThemeContext };
export default ThemeProvider;
